import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ArrowRight, Heart, Users, MessageCircle, Sparkles } from "lucide-react";

// Defaults show if no override is saved in the admin panel
const DEFAULTS = {
  hero_eyebrow: "Approved by the State of Florida",
  hero_title_prefix: "Our Overdose Awareness",
  hero_title_italic: "license plate",
  hero_title_suffix: "has been approved.",
  hero_body: "Help us reach 3,000 pre-sold vouchers at $33 each. Every voucher brings these plates one step closer to Florida roads - and overdose awareness one step closer to every driver behind us.",
  hero_primary_label: "Get Your Voucher",
  hero_primary_href: "/overdose-awareness-license-plates",
  mission_eyebrow: "Our Mission",
  mission_title_line1: "Changing Minds,",
  mission_title_line2: "Changing Lives.",
  mission_body_1: "Stigma is a public health issue, and it contributes to high rates of death, incarceration, and mental health concerns. It damages self-esteem and relationships, and will keep many from seeking much-needed treatment for addiction and mental health issues.",
  mission_body_2: "According to the World Health Organization (WHO), stigma is a major cause of discrimination and exclusion, and it contributes to the abuse of human rights. Furthermore, stigma is rarely based on facts.",
  quote_text: "Change the story and you change perception. Change perception and you change the world.",
  quote_author: "Jean Houston",
  final_cta_title: "Stand with us. Reverse the stigma.",
  final_cta_body: "Whether you're a person in recovery, a loved one, an ally, or a partner organization - your voice matters. Reach out, share your story, and help us change perception.",
};

// Support editable "hero_title" as one field OR fall back to prefix/italic/suffix split
function renderHeroTitle(c) {
  if (c.hero_title) return c.hero_title;
  return (
    <>
      {c.hero_title_prefix || DEFAULTS.hero_title_prefix}{" "}
      <span className="italic text-[var(--rts-blue-light)]">
        {c.hero_title_italic || DEFAULTS.hero_title_italic}
      </span>{" "}
      {c.hero_title_suffix || DEFAULTS.hero_title_suffix}
    </>
  );
}

export default function Home() {
  const [c, setC] = useState({}); // content overrides
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/site-content/home");
        if (data && typeof data === "object") setC(data);
      } catch {}
    })();
  }, []);
  const t = (k) => c[k] || DEFAULTS[k];

  return (
    <div data-testid="home-page">
      {/* ANNOUNCEMENT HERO */}
      <section className="relative overflow-hidden text-white bg-grain" style={{ background: "linear-gradient(135deg, #0b6c84 0%, #1ea7c4 55%, #5ec8df 100%)" }} data-testid="hero-announcement">
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-28 relative grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/25 text-xs uppercase tracking-[0.2em] font-semibold mb-6">
              <Sparkles className="w-3.5 h-3.5" /> {t("hero_eyebrow")}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6">
              {renderHeroTitle(c)}
            </h1>
            <p className="text-base md:text-lg text-white/85 max-w-xl mb-8 leading-relaxed whitespace-pre-line">
              {t("hero_body")}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to={t("hero_primary_href")}
                className="inline-flex items-center gap-2 bg-[var(--rts-red)] hover:bg-[var(--rts-red-deep)] text-white px-6 py-3.5 rounded-full font-bold shadow-xl shadow-black/20 transition-all hover:-translate-y-0.5"
                data-testid="hero-cta-voucher"
              >
                {t("hero_primary_label")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3.5 rounded-full font-semibold border border-white/30 backdrop-blur transition"
                data-testid="hero-cta-about"
              >
                Learn About PARS
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative animate-float">
              <div className="absolute -inset-4 bg-white/10 rounded-3xl blur-2xl" />
              <img src="/license-plate.jpg" alt="Overdose Awareness Florida license plate design" className="relative w-full rounded-2xl shadow-2xl ring-1 ring-white/30" data-testid="hero-plate-image" />
            </div>
          </div>
        </div>
        <svg className="block w-full" viewBox="0 0 1440 80" preserveAspectRatio="none" aria-hidden="true">
          <path fill="#ffffff" d="M0 40 C 240 80 480 0 720 40 C 960 80 1200 0 1440 40 L 1440 80 L 0 80 Z"/>
        </svg>
      </section>

      {/* MISSION SECTION */}
      <section className="bg-white py-20 lg:py-28" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <span className="inline-block text-xs font-bold tracking-[0.25em] uppercase text-[var(--rts-red)] mb-4">
              {t("mission_eyebrow")}
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[var(--rts-ink)] mb-6">
              {t("mission_title_line1")}<br/>
              <span className="text-[var(--rts-blue-deep)] italic">{t("mission_title_line2")}</span>
            </h2>
            <p className="text-base text-[var(--rts-ink-soft)] leading-relaxed mb-5 whitespace-pre-line">{t("mission_body_1")}</p>
            <p className="text-base text-[var(--rts-ink-soft)] leading-relaxed mb-8 whitespace-pre-line">{t("mission_body_2")}</p>
            <Link to="/connect-with-us" className="inline-flex items-center gap-2 bg-[var(--rts-blue-deep)] hover:bg-[var(--rts-blue)] text-white px-6 py-3.5 rounded-full font-bold shadow-lg transition-all hover:-translate-y-0.5" data-testid="mission-cta-connect">
              Connect with Us <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative">
              <div className="absolute -bottom-6 -right-6 w-3/4 h-3/4 bg-[var(--rts-blue-pale)] rounded-3xl -z-0" />
              <img src="/mission-photo.jpg" alt="Community event raising awareness" className="relative w-full rounded-3xl shadow-xl object-cover" data-testid="mission-image" />
            </div>
          </div>
        </div>
      </section>

      {/* PILLAR QUOTE */}
      <section className="bg-[var(--rts-blue-pale)] py-20" data-testid="quote-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="text-7xl font-display text-[var(--rts-red)] leading-none mb-4">"</div>
          <blockquote className="font-display text-2xl sm:text-3xl lg:text-4xl text-[var(--rts-ink)] leading-tight italic font-semibold whitespace-pre-line">
            {t("quote_text")}
          </blockquote>
          <p className="mt-6 text-sm uppercase tracking-[0.3em] text-[var(--rts-blue-deep)] font-bold">- {t("quote_author")}</p>
        </div>
      </section>

      {/* HOW WE HELP */}
      <section className="bg-white py-20 lg:py-24" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-bold tracking-[0.25em] uppercase text-[var(--rts-red)] mb-3">What We Do</span>
            <h2 className="font-display text-4xl sm:text-5xl text-[var(--rts-ink)]">Three pillars. One movement.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: MessageCircle, title: "Reframe the Conversation", copy: "We replace shame-based language with facts, empathy, and the lived experience of real people in recovery." },
              { icon: Users, title: "Build Community", copy: "Through events, partnerships and storytelling, we connect families, advocates and providers across Florida." },
              { icon: Heart, title: "Save Lives", copy: "Initiatives like the Overdose Awareness license plate fund outreach that reaches people before crisis hits." },
            ].map((p, i) => (
              <div key={p.title} className="group relative bg-white border-2 border-[var(--rts-blue-pale)] rounded-2xl p-8 hover:border-[var(--rts-blue)] hover:-translate-y-1 transition-all" style={{ animationDelay: `${i * 100}ms` }} data-testid={`pillar-${i}`}>
                <div className="w-14 h-14 rounded-2xl bg-[var(--rts-blue-deep)] text-white grid place-items-center mb-5 group-hover:bg-[var(--rts-red)] transition-colors">
                  <p.icon className="w-7 h-7" />
                </div>
                <h3 className="font-display text-2xl text-[var(--rts-ink)] mb-3">{p.title}</h3>
                <p className="text-[var(--rts-ink-soft)] text-sm leading-relaxed">{p.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[var(--rts-ink)] text-white py-20" data-testid="final-cta">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="font-display text-4xl sm:text-5xl mb-5 whitespace-pre-line">{t("final_cta_title")}</h2>
          <p className="text-white/70 max-w-2xl mx-auto mb-8 leading-relaxed whitespace-pre-line">{t("final_cta_body")}</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/connect-with-us" className="bg-[var(--rts-red)] hover:bg-[var(--rts-red-deep)] px-7 py-3.5 rounded-full font-bold transition hover:-translate-y-0.5 shadow-lg" data-testid="final-cta-connect">
              Get in Touch
            </Link>
            <Link to="/overdose-awareness-license-plates" className="bg-white/10 hover:bg-white/20 border border-white/30 px-7 py-3.5 rounded-full font-bold transition" data-testid="final-cta-plate">
              Reserve a Plate Voucher
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
